import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, mediaUrl, type MemberProfile } from '../lib/api';
import { ARTICLE_STATUS, DISCIPLINES, ago, dNum, euro, plural, timeLabel, topDisciplines, wd } from '../lib/domain';
import { dc } from '../lib/style';
import { useMe, useToast } from '../hooks';
import { CloseButton, Avatar, DiscIcon, ErrorBox, Icon, Loading } from '../ui';
import { useBarTitle } from '../barTitle';

export default function MemberDetail() {
  useBarTitle('Mitglied');
  const { id } = useParams();
  const { data: me } = useMe();
  const qc = useQueryClient();
  const toast = useToast();
  const [confirm, setConfirm] = useState(false);
  const {
    data: m,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['member', Number(id)],
    queryFn: () => api<MemberProfile>(`/members/${id}`),
  });
  const setStatus = useMutation({
    mutationFn: (status: 'active' | 'former') => api(`/members/${id}`, { method: 'PATCH', body: { status } }),
    onSuccess: (_d, s) => {
      setConfirm(false);
      void qc.invalidateQueries({ queryKey: ['member', Number(id)] });
      void qc.invalidateQueries({ queryKey: ['members'] });
      toast(s === 'former' ? 'Als ehemalig markiert – abgemeldet und keine E-Mails mehr' : 'Wieder aktiv');
    },
    onError: e => toast(e.message),
  });
  const invite = useMutation({
    mutationFn: () => api(`/members/${id}/invite`, { method: 'POST' }),
    onSuccess: () => toast('Einladung per E-Mail verschickt'),
    onError: e => toast(e.message),
  });
  if (isLoading) return <Loading />;
  if (error || !m) return <ErrorBox error={error} />;
  const name = `${m.first_name} ${m.last_name}`;
  const own = m.id === me?.id;
  const top = topDisciplines(m.disciplines, 8);
  const c = m.contact;
  const address =
    c &&
    [c.street && `${c.street} ${c.house_number ?? ''}`.trim(), [c.zip, c.city].filter(Boolean).join(' ')]
      .filter(Boolean)
      .join(', ');
  return (
    <article className="drawer-page">
      <div className="d-head">
        <CloseButton to="/mitglieder" label="Zurück zur Mitgliederliste" />
        <Avatar person={{ id: m.id, name, photo: m.photo }} size="lg" />
        <p className="eyebrow">
          {m.status === 'former'
            ? 'Ehemaliges Mitglied · nur für Admins sichtbar'
            : m.role === 'admin'
              ? 'Admin'
              : 'Mitglied'}
        </p>
        <h1 className="d-title">{name}</h1>
        <p className="d-when">
          {plural(m.total, 'Teilnahme', 'Teilnahmen')}
          {m.shared ? ` · ${m.shared} davon mit dir` : ''}
        </p>
      </div>
      <div className="d-body">
        {m.can_edit && (
          <div>
            <div className="d-actions">
              <Link className="btn btn-navy btn-sm" to={`/mitglieder/${m.id}/bearbeiten`}>
                <Icon name="edit" sw={1.8} />
                Bearbeiten
              </Link>
              {!own && m.status === 'active' && m.contact?.email && (
                <button className="btn btn-ghost btn-sm" disabled={invite.isPending} onClick={() => invite.mutate()}>
                  <Icon name="mail" sw={1.8} />
                  {m.last_login_at ? 'Einladung schicken' : 'Einladung (erneut) schicken'}
                </button>
              )}
              {!own &&
                (m.status === 'former' ? (
                  <button className="btn btn-ghost btn-sm" onClick={() => setStatus.mutate('active')}>
                    Wieder aktiv setzen
                  </button>
                ) : (
                  <button className="btn btn-ghost btn-sm" onClick={() => setConfirm(true)}>
                    Als ehemalig markieren
                  </button>
                ))}
            </div>
            {confirm && (
              <div className="confirm" role="alert">
                <p>
                  {name} als ehemalig markieren? Die Person wird überall abgemeldet, bekommt keine E-Mails mehr und ist
                  für andere nicht mehr sichtbar. Termine und Teilnahmen bleiben erhalten.
                </p>
                <button className="btn btn-ghost btn-sm" onClick={() => setConfirm(false)}>
                  Abbrechen
                </button>
                <button className="btn btn-danger btn-sm" onClick={() => setStatus.mutate('former')}>
                  Als ehemalig markieren
                </button>
              </div>
            )}
          </div>
        )}
        {c && (
          <section className="panel contact">
            <h2 className="sec-h">{own ? 'Deine Kontaktdaten' : 'Kontakt'}</h2>
            {/* like a phone's contacts: an icon instead of the field name, one line each, tap to mail or call */}
            <ul className="contact-list">
              <li>
                <Icon name="mail" sw={1.7} />
                <span className="sr-only">E-Mail:</span>
                {c.email ? <a href={`mailto:${c.email}`}>{c.email}</a> : <span className="muted">keine E-Mail</span>}
              </li>
              <li>
                <Icon name="phone" sw={1.7} />
                <span className="sr-only">Telefon:</span>
                {c.phone ? (
                  <a href={`tel:${c.phone.replace(/[^\d+]/g, '')}`}>{c.phone}</a>
                ) : (
                  <span className="muted">kein Telefon</span>
                )}
              </li>
              <li>
                <Icon name="pin" sw={1.7} />
                <span className="sr-only">Adresse:</span>
                {address || <span className="muted">keine Adresse</span>}
              </li>
            </ul>
            <dl className="facts">
              {m.last_login_at !== undefined && (
                <div>
                  <dt>Zuletzt angemeldet</dt>
                  <dd>
                    {m.last_login_at
                      ? `${dNum(new Date(m.last_login_at))} · ${ago(new Date(m.last_login_at))}`
                      : 'noch nie'}
                  </dd>
                </div>
              )}
            </dl>
            {own && (
              <p className="note">
                Ändern kannst du deine Daten im <Link to="/profil">Profil</Link>.
              </p>
            )}
          </section>
        )}
        {top.length > 0 && (
          <section>
            <h2 className="sec-h">Aktiv in</h2>
            <div className="bars">
              {top.map(([k, v]) => (
                <div key={k} className="bar" style={dc(k)}>
                  <span>{DISCIPLINES[k] ?? k}</span>
                  <span className="track">
                    <i style={{ width: `${(v / top[0][1]) * 100}%` }} />
                  </span>
                  <b>{v}</b>
                </div>
              ))}
            </div>
          </section>
        )}
        <section>
          <h2 className="sec-h">
            Kommende Termine <span>{m.upcoming.length}</span>
          </h2>
          {m.upcoming.length ? (
            m.upcoming.map(e => {
              const d = new Date(e.starts_at);
              return (
                <Link key={e.id} className="row" to={`/termine/${e.id}`} style={dc(e.discipline)}>
                  <DiscIcon disc={e.discipline} size="sm" />
                  <span className="row-txt">
                    <b>{e.title}</b>
                    <small>
                      {wd(d)} {dNum(d)} · {timeLabel(e)}
                      {e.location ? ` · ${e.location}` : ''}
                    </small>
                  </span>
                </Link>
              );
            })
          ) : (
            <p className="note">Keine kommenden Zusagen.</p>
          )}
        </section>
        {m.mates.length > 0 && (
          <section>
            <h2 className="sec-h">Oft dabei mit</h2>
            {m.mates.map(x =>
              x.id ? (
                <Link key={x.id} className="row" to={`/mitglieder/${x.id}`}>
                  <Avatar person={x} />
                  <span className="row-txt">
                    <b>{x.name}</b>
                    <small>{plural(x.n, 'gemeinsamer Termin', 'gemeinsame Termine')}</small>
                  </span>
                </Link>
              ) : null,
            )}
          </section>
        )}
        {m.articles.length > 0 && (
          <section>
            <h2 className="sec-h">
              Im Flohmarkt <span>{m.articles.length}</span>
            </h2>
            <div className="thumbs2">
              {m.articles.map(a => (
                <Link key={a.id} to={`/flohmarkt/${a.id}`}>
                  <div className="ph-img">
                    {a.status !== 'active' && (
                      <span className={`st ${a.status === 'sold' ? 'st-sold' : 'st-res'}`}>
                        {ARTICLE_STATUS[a.status]}
                      </span>
                    )}
                    {a.thumb ? (
                      <img className="loaded" src={mediaUrl(a.thumb)!} alt="" loading="lazy" />
                    ) : (
                      <Icon name="tag" />
                    )}
                  </div>
                  <small>
                    {a.title} · {euro(a.price)}
                  </small>
                </Link>
              ))}
            </div>
          </section>
        )}
      </div>
    </article>
  );
}
